import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Button } from '@mantine/core';
import { MasterTradeParams } from 'app/models/master-trade-params';
import MainTable from 'app/views/components/Table/MainTable';
import { AllocationAmountUpdate, columnDefSubTradeAllocation, FacilityAllocationHandlers, SaveAllocationRow, SubTradeParams } from 'app/models/sub-trade-params';
import { formatPriceWithoutCurrency } from 'app/utils/util-functions';
import { useSelector, useDispatch } from 'react-redux';
import { getSubTrades } from 'app/state/ducks/sub-trades/selectors';
import { getLockedFacilities } from 'app/state/ducks/facilities/selectors';
import { getTradedFacilities } from 'app/state/ducks/traded-facilities/selectors';
import { columnDefsAllocation } from 'app/views/pages/trade-entry/detail-trade/sub-trades/sub-trade-allocation-columnDefs';
import { saveSubTradeAllocation } from 'app/state/ducks/sub-trades/thunks';
import { ErrorNotification, SuccessNotification } from 'app/views/components/notifications/notification';
import { TradeEntryEntitySearch } from 'app/views/pages/trade-entry/detail-trade/sub-trades/trade-entry-entity-allocation';
import DeleteConfirmation from 'app/views/components/delete-confirmation/DeleteConfirmation';
import { EntityParams } from 'app/models/entity-params';
import { clampAmountToFacilityRemaining, computeProratedAmounts, computeUnallocatedAmounts, generateUpdateAllocationsRowsForEntity, sortBySystemId, transformDataAllocation } from 'app/views/pages/trade-entry/detail-trade/sub-trades/sub-trade-utils';
import { loadAllSignableDocuments } from 'app/state/ducks/signable-document/thunks';
import { setSignableDocumentsGenerating } from 'app/state/ducks/signable-document/actions';
import { ContractType, DocumentStateTypes } from 'app/models/common-types';
import { getDocuments } from 'app/state/ducks/documents/selectors';
import { v4 as uuidv4 } from 'uuid'
import { Document } from 'app/models/document-params'
import { addTemporarySubTrade, deleteSubTrade } from 'app/state/ducks/sub-trades/actions';
import { addTemporaryTradedFacility, deleteTradedFacilities, updateTradedFacilityAmount } from 'app/state/ducks/traded-facilities/actions';
import { FundGroup } from 'app/models/fund-group';
import { getFundGroupsForFundManager } from 'app/state/ducks/fund-groups/selectors';
import { loadFundGroupsByManager } from 'app/state/ducks/fund-groups/thunks';
import { loadEntities } from 'app/state/ducks/entities/thunks';
import { loadAllEntityTypes } from 'app/state/ducks/entity-type/thunks';
import { useTradePageEntities, useTradePageFacilities } from 'app/views/pages/trade-entry/detail-trade/use-trade-page-lookups';
import { useTradeRefresh } from 'app/views/pages/trade-entry/detail-trade/use-trade-refresh';
import { QuickAllocationDropdown } from 'app/views/pages/trade-entry/detail-trade/sub-trades/fund-groups/quick-allocation-dropdown';
import { ManageFundGroupsModal } from 'app/views/pages/trade-entry/detail-trade/sub-trades/fund-groups/manage-fund-groups-modal';
import { FUND_GROUP_COPY } from 'app/views/pages/trade-entry/detail-trade/sub-trades/fund-groups/fund-group-copy';
import { entitiesManagedBy, entitiesToAppend, indexEntities, resolveTradeFundManager } from 'app/views/pages/trade-entry/detail-trade/sub-trades/fund-groups/fund-group-utils';
interface DetailPanelProps {
  row: MasterTradeParams;
  refinedSubTrades: SubTradeParams[];
  closeUpdateAllocation: () => void;
  onRegisterFacilityActions?: (handlers: FacilityAllocationHandlers | null) => void;
}

const UpdateSubTradeAllocation: React.FC<DetailPanelProps> = ({ row, refinedSubTrades, closeUpdateAllocation, onRegisterFacilityActions }) => {
  const dispatch = useDispatch();
  const { refreshTrade } = useTradeRefresh();
  // The fund picker and the allocation rows need every entity the fund
  // manager manages and their types; the tab itself only holds the trade's
  // own (ADR_LOS-030). This editor mounts when it is opened.
  useEffect(() => {
    dispatch(loadEntities() as any);
    dispatch(loadAllEntityTypes() as any);
  }, [dispatch]);
  const subTrades: SubTradeParams[] = useSelector(getSubTrades);
  const allEntities = useTradePageEntities();
  const facilities = useTradePageFacilities();
  const allTradedFacilities = useSelector(getTradedFacilities);
  const [isLoading, setIsLoading] = useState(false);
  const [filteredEntities, setFilteredEntities] = useState<EntityParams[]>([]);
  const [allocationData, setAllocationData] = useState<columnDefSubTradeAllocation[]>([]);
  const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<{ rowIndex: number; row: any } | null>(null);
  const allAttachments = useSelector(getDocuments)
  const [attachementsMasterTrade, setAttachementsMasterTrade] = useState<Document[]>([])
  const lockedFacilities = useSelector(getLockedFacilities)
  const allFacilities = useMemo(() => [...facilities, ...lockedFacilities], [facilities, lockedFacilities]);
  const [manageGroupsOpen, setManageGroupsOpen] = useState(false);

  // Quick Allocation (LOS-6734): fund groups belong to the trade's fund-manager counterparty.
  const fundManager = useMemo(() => resolveTradeFundManager(row, allEntities), [row, allEntities]);
  const fundGroups: FundGroup[] = useSelector((state: any) => getFundGroupsForFundManager(state, fundManager?.id));
  const managedEntities = useMemo(() => entitiesManagedBy(fundManager, allEntities), [fundManager, allEntities]);
  const entitiesById = useMemo(() => indexEntities(allEntities), [allEntities]);

  useEffect(() => {
    if (!fundManager?.id) return;
    dispatch(loadFundGroupsByManager(fundManager.id) as any).then((result: any) => {
      if (result && !result.success) {
        ErrorNotification({ title: FUND_GROUP_COPY.loadFailedTitle, message: result.payload });
      }
    });
  }, [dispatch, fundManager?.id]);

  useEffect(() => {
    const documentStateType = DocumentStateTypes[ContractType.MasterTrade]
    setAttachementsMasterTrade(allAttachments[documentStateType] ?? [])
  }, [allAttachments])


  const reloadData = async () => {
    try {
      await Promise.all([
        // this trade — its sub trades, traded facilities, attachments and the
        // entities an allocation brings in — in its one read (ADR_LOS-030);
        // the signable documents for the Documents tab as before
        refreshTrade(),
        dispatch(loadAllSignableDocuments()),
      ]);
    } catch (error) {
      console.error('Error reloading data:', error);
    }
  };

  const refinedFacilities = useMemo(() => {
    return allTradedFacilities
      .filter(tf => tf.contractId?.id === row.id)
      .map(facility => ({
        ...facility,
        facilityDetails: allFacilities.find(f => f.id === facility.facilityId.id) || null,
      }));
  }, [allTradedFacilities, allFacilities, row.id]);


  // computedEntities: filter out seller and buyer.
  const computedEntities = useMemo(() => {
    const matchingSeller = allEntities.find(entity => entity.id === row.sellerId?.id);
    const matchingBuyer = allEntities.find(entity => entity.id === row.buyerId?.id);
    return allEntities.filter(
      entity =>
        (entity.fundManager === matchingSeller?.entityName ||
          entity.fundManager === matchingBuyer?.entityName) &&
        (entity.id !== matchingSeller?.id && entity.id !== matchingBuyer?.id)
    );
  }, [allEntities, row]);

  useEffect(() => {
    setFilteredEntities(computedEntities.filter(entity =>
      !refinedSubTrades.some((st: any) => st.sellerId?.id === entity.id || st.buyerId?.id === entity.id)
    ).sort((a, b) => {
      return a.entityName.localeCompare(b.entityName)
    }))
  }, [computedEntities, refinedSubTrades]);

  // Callback for deleting a row.
  const handleDelete = async (rowIndex: number, row: any) => {
    const subTradeId = subTrades.find(st => st.buyerId?.id === row.entityId.id || st.sellerId?.id === row.entityId.id)?.id
    const subTradeDoc = attachementsMasterTrade.find((doc) => doc.contractId.id === subTradeId)
    if (subTradeDoc) {
      ErrorNotification({
        title: 'Cannot delete sub-trade allocation',
        message: 'This sub-trade has attached documents and cannot be removed.',
      });
    } else {
      const findIfExistDatabase = subTrades.find(st => st.buyerId?.id === row.entityId.id || st.sellerId?.id === row.entityId.id)
      if (findIfExistDatabase) {
        setRowToDelete({ rowIndex, row });
        setDeleteConfirmationVisible(true);
        return
      }
      deleteEntity(rowIndex, row);
    }

  };

  const deleteEntity = async (rowIndex: number, row: any) => {
    const deletedEntity = computedEntities.find(entity => entity.id === row.entityId.id);
    if (!deletedEntity) return
    const newFilteredEntities = [...filteredEntities, deletedEntity]
    setFilteredEntities(
      newFilteredEntities.sort((a, b) =>
        a.entityName.localeCompare(b.entityName)
      ))
    setAllocationData(prev => deleteRowsFromAllocation(prev, rowIndex));
    dispatch(deleteSubTrade([{ id: row.contractId?.id }]));
    dispatch(deleteTradedFacilities({ id: row.contractId.id }));
  }

  // Append entities as new sub-trade rows with 0 amounts. One temporary sub-trade id per entity;
  // functional state updates so a whole fund group lands in one render without clobbering.
  const addEntitiesForAllocationsUpdate = (entities: EntityParams[]) => {
    if (entities.length === 0) return;
    const masterTradeId = { id: row.id ?? '', admin: row.agencyAdmin ?? '' }
    const newRows: columnDefSubTradeAllocation[] = [];
    entities.forEach(selectedEntity => {
      const subTradeId = uuidv4();
      // Generate rows first, then fix the contractId to use the temporary subTradeId
      generateUpdateAllocationsRowsForEntity(selectedEntity, refinedSubTrades, refinedFacilities, masterTradeId)
        .forEach(generated => newRows.push({ ...generated, contractId: { id: subTradeId, admin: masterTradeId.admin } }));
      dispatch(addTemporarySubTrade({ facilityIds: refinedFacilities.map(rf => rf.facilityId), tradeAmount: 0, contractType: "SubTradeFacility", entityId: selectedEntity.id, masterTradeId, subTradeId }));
      refinedFacilities.forEach(facility => {
        dispatch(addTemporaryTradedFacility({ facilityId: facility.facilityId, tradeAmount: 0, contractType: "SubTradeFacility", entityId: selectedEntity.id, masterTradeId, subTradeId }));
      })
    });
    const addedIds = new Set(entities.map(e => e.id));
    setAllocationData(prev => [...prev, ...newRows]);
    setFilteredEntities(prev => prev.filter(entity => !addedIds.has(entity.id)));
  }

  const addEntityForAllocationsUpdate = (value: string | null) => {
    const selectedEntity = filteredEntities.find(entity => entity.id === value);
    if (selectedEntity) addEntitiesForAllocationsUpdate([selectedEntity]);
  }

  // Quick Allocation: append the group's entities not yet on the table (matched by entity ID),
  // at 0 amounts. Candidates are every entity the fund manager manages — the same list the group
  // was built from — and the table's current entity ids prevent duplicates. Nothing is persisted
  // until the user clicks Save.
  const applyFundGroup = (group: FundGroup) => {
    const current = new Set(allocationData.map(r => r.entityId?.id).filter((id): id is string => !!id));
    const toAppend = entitiesToAppend(group, current, managedEntities);
    if (toAppend.length === 0) {
      ErrorNotification({ title: FUND_GROUP_COPY.quickAllocation, message: FUND_GROUP_COPY.appliedNothing });
      return;
    }
    addEntitiesForAllocationsUpdate(toAppend);
    SuccessNotification({ title: FUND_GROUP_COPY.appliedTitle, message: FUND_GROUP_COPY.applied(toAppend.length, group.name) });
  }

  // Initialize allocation data from existing SubTrades on component mount only
  const initialAllocationRows = useMemo(() => {
    if (refinedSubTrades.length === 0) return [];

    const subTradesEntities = computedEntities.filter(entity =>
      refinedSubTrades.some((st: any) => st.sellerId?.id === entity.id || st.buyerId?.id === entity.id)
    );

    // Sort existing entities by systemId for consistency when loading saved data
    const sortedEntities = [...subTradesEntities].sort((a, b) => sortBySystemId(a, b, refinedSubTrades));

    return sortedEntities.flatMap((entity) =>
      generateUpdateAllocationsRowsForEntity(entity, refinedSubTrades, refinedFacilities, { id: row.id ?? '', admin: row.agencyAdmin ?? '' })
    );
  }, []); // Empty dependency array - only runs once on mount

  useEffect(() => {
    if (initialAllocationRows.length > 0 && allocationData.length === 0) {
      setAllocationData(initialAllocationRows);
    }
  }, [initialAllocationRows]);

  // Reloading data when component unmounts, because we are changing redux store at times, if user didnt click SAVE we discard changes
  useEffect(() => {
    return () => {
      reloadData();
    }
  }, [])

  // Update Redux state for live totals calculation
  // Match by contractId (which contains the subTradeId UUID for temporary entities)
  const syncTradedFacilityAmount = (row: columnDefSubTradeAllocation, newAmount: number) => {
    const tradedFacilityIndex = allTradedFacilities.findIndex(tf =>
      tf.contractId?.id === row.contractId?.id &&
      tf.facilityId?.id === row.facilityWithAmount?.facilityId?.id
    );

    if (tradedFacilityIndex !== -1) {
      dispatch(updateTradedFacilityAmount({
        index: tradedFacilityIndex,
        tradeAmount: newAmount
      }));
    } else if (row.contractId?.id && row.facilityWithAmount?.facilityId) {
      dispatch(addTemporaryTradedFacility({
        facilityId: row.facilityWithAmount.facilityId,
        tradeAmount: newAmount,
        contractType: 'SubTradeFacility',
        subTradeId: row.contractId.id,
      }));
    }
  };

  // Apply a batch of amount updates to local state (with modification timestamps)
  const applyLocalAmountUpdates = (updates: AllocationAmountUpdate[]) => {
    setAllocationData(prev => {
      const updated = [...prev];
      updates.forEach(({ rowIndex, amount }) => {
        if (!updated[rowIndex]) return;
        updated[rowIndex] = {
          ...updated[rowIndex],
          amount,
          lastModified: Date.now() // Track when this amount was last modified
        };
      });
      return updated;
    });
  };

  // Apply a batch of amount updates to local state AND Redux. For prorate,
  // unallocate and clamp-on-blur, where nothing else dispatches the sync.
  const applyAmountUpdates = (updates: AllocationAmountUpdate[]) => {
    if (updates.length === 0) return;
    applyLocalAmountUpdates(updates);
    updates.forEach(({ rowIndex, amount }) => {
      const row = allocationData[rowIndex];
      if (row) syncTradedFacilityAmount(row, amount);
    });
  };

  // Callback for updating a row's amount while typing. Local state only — the
  // amount cell dispatches the Redux sync itself on each keystroke, so syncing
  // here too would double-dispatch (and could add a duplicate temporary traded
  // facility when the row doesn't exist in the store yet).
  const handleAmountChange = (rowIndex: number, newAmount: number) => {
    if (!allocationData[rowIndex]) return;
    applyLocalAmountUpdates([{ rowIndex, amount: newAmount }]);
  };

  // AC7: on blur, clamp an amount that would push the facility over its traded
  // amount down to the remaining balance. No toast on the happy path — the
  // recalculated totals are the feedback; the clamp gets an error toast.
  const handleAmountBlur = (rowIndex: number, newAmount: number): number => {
    const { amount, clamped } = clampAmountToFacilityRemaining(allocationData, rowIndex, newAmount);
    if (clamped) {
      applyAmountUpdates([{ rowIndex, amount }]);
      ErrorNotification({
        title: 'Amount exceeds facility total traded',
        message: `The amount was reduced to ${formatPriceWithoutCurrency(amount, 2, 2)}, the remaining balance on ${allocationData[rowIndex]?.facilityName}.`,
      });
    }
    return amount;
  };

  // Prorate: split the facility's remaining balance across its unallocated
  // entities (or all of them when every entity already has an amount).
  const handleProrate = (facilityId: string) => {
    applyAmountUpdates(computeProratedAmounts(allocationData, facilityId));
  };

  // Unallocate: reset every entity's amount on the facility to 0.
  const handleUnallocate = (facilityId: string) => {
    applyAmountUpdates(computeUnallocatedAmounts(allocationData, facilityId));
  };

  // Expose Prorate/Unallocate to the "To Be Allocated" panel while this editor
  // is open. The handlers close over allocationData and are recreated every
  // render, so register stable wrappers that read the latest ones from a ref.
  const facilityActionsRef = useRef({ handleProrate, handleUnallocate });
  facilityActionsRef.current = { handleProrate, handleUnallocate };

  // Serialized so the effect below only re-registers when the set of
  // facilities with an editable (non-Settled) row actually changes, not on
  // every keystroke.
  const editableFacilityIdsKey = useMemo(() => {
    const ids = new Set<string>();
    allocationData.forEach(dataRow => {
      const facilityId = dataRow.facilityWithAmount?.facilityId?.id;
      if (facilityId && dataRow.status !== 'Settled') ids.add(facilityId);
    });
    return Array.from(ids).sort().join('|');
  }, [allocationData]);

  useEffect(() => {
    if (!onRegisterFacilityActions) return;
    onRegisterFacilityActions({
      onProrate: facilityId => facilityActionsRef.current.handleProrate(facilityId),
      onUnallocate: facilityId => facilityActionsRef.current.handleUnallocate(facilityId),
      editableFacilityIds: new Set(editableFacilityIdsKey ? editableFacilityIdsKey.split('|') : []),
    });
    return () => onRegisterFacilityActions(null);
  }, [onRegisterFacilityActions, editableFacilityIdsKey]);

  const handleClientIdChange = (rowIndex: number, newClientId: string) => {
    setAllocationData(prev => {
      const updated = [...prev];
      updated[rowIndex] = { ...updated[rowIndex], clientId: newClientId };
      return updated;
    });
  };

  const deleteRowsFromAllocation = (prevData: any[], rowIndex: number) => {
    const start = rowIndex;
    let end = rowIndex;
    while (end + 1 < prevData.length && !prevData[end + 1].entityName) {
      end++;
    }
    return prevData.filter((_, idx) => idx < start || idx > end);
  };

  // Calculate facility allocation status and identify the specific row that caused overallocation
  const facilityAllocationStatus = useMemo(() => {
    const statusMap = new Map<string, { totalAllocated: number; tradedAmount: number; isOverallocated: boolean; culpritRowKey?: string }>();

    // Group rows by facility
    const facilityGroups = new Map<string, typeof allocationData>();
    allocationData.forEach(row => {
      const facilityId = row.facilityWithAmount?.facilityId?.id;
      if (!facilityId) return;

      if (!facilityGroups.has(facilityId)) {
        facilityGroups.set(facilityId, []);
      }
      facilityGroups.get(facilityId)!.push(row);
    });

    // For each facility, calculate total and find the specific row that caused overallocation
    facilityGroups.forEach((rows, facilityId) => {
      const tradedAmount = Math.round((Number(rows[0]?.facilityWithAmount?.total) || 0) * 100) / 100;

      // Sort rows by modification time (earliest first, unmodified rows have no timestamp so they come first)
      const sortedRows = [...rows].sort((a, b) =>
        (a.lastModified || 0) - (b.lastModified || 0)
      );

      let runningTotal = 0;
      let culpritRowKey: string | undefined;

      for (const row of sortedRows) {
        const amount = Number(row.amount) || 0;
        const previousTotal = runningTotal;
        runningTotal += amount;

        const roundedTotal = Math.round(runningTotal * 100) / 100;
        const roundedPrevious = Math.round(previousTotal * 100) / 100;

        // Check if this row pushed us over the limit
        if (roundedPrevious <= tradedAmount && roundedTotal > tradedAmount) {
          // This is the row that caused the overallocation
          culpritRowKey = `${row.contractId?.id}-${row.facilityWithAmount?.facilityId?.id}`;
          break;
        }
      }

      const isOverallocated = Math.round(runningTotal * 100) / 100 > tradedAmount;

      statusMap.set(facilityId, {
        totalAllocated: runningTotal,
        tradedAmount,
        isOverallocated,
        culpritRowKey
      });
    });

    return statusMap;
  }, [allocationData]);

  const tableData = useMemo(() => {
    return allocationData.map(row => {
      const facilityId = row.facilityWithAmount?.facilityId?.id;
      const rowKey = `${row.contractId?.id}-${row.facilityWithAmount?.facilityId?.id}`;
      const facilityStatus = facilityId ? facilityAllocationStatus.get(facilityId) : null;

      // Only mark this specific row as overallocated if it's the one that pushed us over
      const isOverallocated = facilityStatus?.culpritRowKey === rowKey;

      return {
        ...row,
        isOverallocated,
        onAmountChange: handleAmountChange,
        onAmountBlur: handleAmountBlur,
        onDelete: handleDelete,
        onClientIdChange: handleClientIdChange,
      };
    });
  }, [allocationData, facilityAllocationStatus]);


  const saveAllocation = async (allocationData: SaveAllocationRow[]) => {
    const data = transformDataAllocation(allocationData, { id: row.id ?? '', admin: row.agencyAdmin ?? '' });
    setIsLoading(true);
    dispatch(setSignableDocumentsGenerating(true));
    try {
      const response: any = await dispatch(saveSubTradeAllocation(data));
      if (response?.success) {
        await reloadData()
        SuccessNotification({ title: 'Saved Sub Trade Allocation' });
      } else {
        ErrorNotification({
          title: 'Sub Trade Allocation Failed to Save',
          message: response.payload ?? 'Check data and try again',
        });
      }
    } catch {
      ErrorNotification({
        title: 'Sub Trade Allocation Failed to Save',
        message: 'Check data and try again',
      });
    } finally {
      dispatch(setSignableDocumentsGenerating(false));
      closeUpdateAllocation();
      setIsLoading(false);
    }
  };

  // Filter out entities already in tableData.
  const tableEntityNamesSet = useMemo(() => new Set(tableData.map(trade => trade.entityName)), [tableData]);
  const filteredFirstArray = useMemo(
    () => filteredEntities.filter(item => !tableEntityNamesSet.has(item.entityName)),
    [filteredEntities, tableEntityNamesSet]
  );

  return (
    <div className="allocation-overview">
      {/* Sticky header (PO-146): Quick Allocation, entity search, Cancel and Save stay reachable
          however long the grid gets. */}
      <div className="update-allocation-view">
        <div className="update-allocations-field-text">Update Allocations</div>
        <div className="update-allocation-controls">
          {fundManager && (
            <QuickAllocationDropdown
              groups={fundGroups}
              entitiesById={entitiesById}
              onApply={applyFundGroup}
              onManage={() => setManageGroupsOpen(true)}
            />
          )}
          <div className="update-allocation-search">
            <TradeEntryEntitySearch
              data={filteredFirstArray}
              selectEntity={addEntityForAllocationsUpdate}
            />
          </div>
          <Button
            className="add-modal-fee-cancel-button"
            onClick={async () => {
              // Reload data on cancel to get store in sync and discard changes
              reloadData();
              closeUpdateAllocation();
              setAllocationData(initialAllocationRows);
            }}
          >
            <div className="add-modal-fee-cancel-button-text">Cancel</div>
          </Button>
          <Button
            className="add-modal-fee-save-button"
            disabled={isLoading}
            onClick={() => {
              saveAllocation(allocationData as unknown as SaveAllocationRow[]);
            }}
          >
            <div className="add-modal-fee-save-button-text">Save</div>
          </Button>
        </div>
      </div>
      <div style={{ width: '100%' }}>
        <MainTable
          tableName="Allocation"
          columnDefs={columnDefsAllocation}
          withPagination={false}
          data={tableData ?? []}
          isLoading={isLoading}
          enableTopToolbar={false}
          setRow={() => { }}
          headerBackgroundColor="#F0EEEE"
          enableColumnOrdering={false}
          enableColumnResizing={false}
          enableGrouping={true}
          enableColumnFilters={false}
          enableColumnActions={false}
          enableFilters={false}
          enableSorting={false}
          enableFullScreenToggle={false}
          enableDensityToggle={false}
          enableHiding={false}
          border={true}
          fromTradeEntry={false}
        />
      </div>
      {fundManager && (
        <ManageFundGroupsModal
          opened={manageGroupsOpen}
          onClose={() => setManageGroupsOpen(false)}
          fundManagerId={fundManager.id ?? ''}
          groups={fundGroups}
          entities={managedEntities}
        />
      )}
      {deleteConfirmationVisible && (
        <DeleteConfirmation
          onConfirm={() => {
            if (!rowToDelete)
              return
            deleteEntity(rowToDelete.rowIndex, rowToDelete.row);
            setRowToDelete(null);
            setDeleteConfirmationVisible(false);
          }}
          onCancel={() => {
            setDeleteConfirmationVisible(false);
          }}
        />
      )};
    </div>
  )
}

export default UpdateSubTradeAllocation;


